import { Button } from '@citizen-walk/shared-ui';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AdminAuthContext } from '../features/auth';
import { districtNavGroups, useDistricts } from '../features/districts-admin';
import { Breadcrumbs } from './Breadcrumbs';
import { DistrictSwitcher } from './DistrictSwitcher';
import { UpdateBanner } from './UpdateBanner';

/**
 * 管理アプリ全体のサイドバー付きレイアウト。
 *
 * Sprint BG-Menu (本 commit、第 1 段 + 第 2 段): 旧 3 グループ (運用 / マスター管理 / サポート) を
 * 「役割別 4 グループ」に再構成。
 *
 *   - ホーム
 *   - 担当地区 (district admin / master 共通、選択時のみサブメニュー展開)
 *   - 全地区 (master only、地区一覧 + master だけ存在する CRUD)
 *   - テンプレ・マスタ (master only、3 ブロック構成)
 *   - 権限・監査 (master only、ユーザー管理 + 操作ログ)
 *   - 使い方ガイド
 *
 * 用語統一: マスタ側 (M1〜M10) は「テンプレ」または「マスタ」、地区側は「運用中」。
 * URL は変更なし (第 3 段で対応予定、既存 URL ブックマーク維持)。
 *
 * 担当地区サブメニュー展開: 現在の URL が `/districts/:id/*` のとき、該当地区配下の
 * サブメニュー項目 (概要 / 投稿モデレーション / お知らせ / アンケート / 検証 / セッション /
 * 入力項目 / AI 設定 / 表示設定 / データ移動) を展開表示する。
 */

interface NavItem {
  to: string;
  label: string;
  description?: string;
}

interface SubGroup {
  /** 任意のサブグループ見出し (テンプレ・マスタ内の「AI 設定マスタ」等)。 */
  title?: string;
  items: NavItem[];
}

interface NavGroup {
  title: string;
  /** group 全体を master role に gate する場合 true。 */
  masterOnly?: boolean;
  /** 単一 items リスト、または subgroups で 2 段構造化。 */
  items?: NavItem[];
  subgroups?: SubGroup[];
}

const HELP_ITEM: NavItem = {
  to: '/help',
  label: '使い方ガイド',
  description: '管理者向けの使い方ガイド',
};

/**
 * master 用「テンプレ・マスタ」グループ。3 ブロック構成。
 * 旧 M1-M9 + ModeProfiles の番号表示を廃止し、ユーザー向けに分かりやすい日本語名に統一。
 */
const TEMPLATE_GROUP: NavGroup = {
  title: 'テンプレ・マスタ',
  masterOnly: true,
  subgroups: [
    {
      title: 'AI 設定マスタ',
      items: [
        { to: '/masters/ai-analysis-profile', label: 'AI 解析 統合エディタ' },
        { to: '/masters/ai-category', label: 'AI カテゴリ' },
        { to: '/masters/ai-output-format', label: 'AI 出力フォーマット' },
        { to: '/masters/ai-dialogue-settings', label: 'AI 対話設定' },
        { to: '/masters/street-survey-templates', label: '机上調査テンプレート' },
      ],
    },
    {
      title: '分析画面マスタ',
      items: [
        { to: '/masters/analysis-display', label: '分析表示設定' },
        // 【廃止予定 / MD-3 で非表示化 2026-07-24】M6 スコア軸定義は runtime 読込みゼロの
        // dead master (監査判定)。route は直 URL 互換のため温存 (App.tsx 参照)。
        // { to: '/masters/score-dimension', label: 'スコア軸定義' },
        { to: '/masters/card-print', label: 'カード印刷テンプレ' },
      ],
    },
    {
      title: 'コンテンツテンプレ',
      items: [
        { to: '/masters/survey-preset', label: 'アンケートテンプレ' },
        { to: '/masters/verification-template', label: '検証テンプレ' },
        { to: '/masters/mode-profiles', label: 'モードプロファイル' },
      ],
    },
  ],
};

const AUDIT_GROUP: NavGroup = {
  title: '権限・監査',
  masterOnly: true,
  items: [
    {
      // 地区の「ユーザー管理」(参加者) と区別するため「管理者アカウント」に改称 (2026-09-07)。
      to: '/users',
      label: '管理者アカウント',
      description: '管理者の招待と権限（マスター／地区）・担当地区の設定',
    },
    { to: '/operation-logs', label: '操作ログ', description: 'お知らせ・セッションなどの操作履歴' },
  ],
};

/**
 * 現在の pathname から /districts/:id/* の id を抽出。`/districts` (一覧) では null。
 * Pure 関数として export し unit test 可能化。
 */
export function extractDistrictIdFromPath(pathname: string): string | null {
  const match = /^\/districts\/([^/]+)(?:\/.*)?$/.exec(pathname);
  if (!match) return null;
  const id = match[1];
  if (!id || id === 'new') return null;
  return id;
}

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  if (pathname === to) return true;
  // /districts と /districts/abc を区別: /districts のみで startsWith すると /districts/abc も
  // active 判定されてしまうため、完全一致 or `${to}/` 始まりに限定する。
  return pathname.startsWith(to + '/');
}

/** /districts/:id の概要ページ active 判定は完全一致のみ (サブパスは別項目で active 化)。 */
function isExactActive(pathname: string, to: string): boolean {
  return pathname === to;
}

function Sidebar({
  ctx,
  open,
  onNavigate,
}: {
  ctx: AdminAuthContext;
  open: boolean;
  onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  const isMaster = ctx.role === 'master';

  // master は全地区参照可、district admin は担当 districts のみ。地区一覧 (Firestore subscribe)
  // を取得しサイドバーの「担当地区」配下に表示する。district admin で districts claim が空なら
  // サイドバーには地区を 1 つも出さない (担当外をうっかり開かせない)。
  const districtsState = useDistricts();
  const visibleDistricts = useMemo(() => {
    if (districtsState.status !== 'ready') return [];
    if (isMaster) return districtsState.items;
    const allow = new Set(ctx.districts);
    return districtsState.items.filter((d) => allow.has(d.id));
  }, [districtsState, isMaster, ctx.districts]);

  const currentDistrictId = extractDistrictIdFromPath(pathname);

  return (
    <aside
      className={`
        fixed md:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200
        transform transition-transform md:transform-none
        ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        flex flex-col h-screen md:h-auto md:min-h-screen
      `}
    >
      <div className="px-4 py-5 border-b border-slate-200">
        <Link
          to="/"
          onClick={onNavigate}
          className="block text-lg font-bold text-slate-900 hover:text-primary-700"
        >
          CitizenWalk
          <span className="ml-1 text-xs text-slate-500 font-normal">Admin</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {/* ホーム */}
        <SidebarGroup title="ホーム">
          <SidebarLink
            to="/"
            label="ダッシュボード"
            active={pathname === '/'}
            onNavigate={onNavigate}
          />
        </SidebarGroup>

        {/* 担当地区 (master / district admin 共通)。
            district admin は担当地区のみ、master は全地区を「全地区」グループで別表示するため
            ここでは件数が多くなりすぎないよう「最近選択した地区」を currentDistrictId で示しつつ、
            それ以外は折り畳みを期待。MVP では全件出すが master は普段「全地区」リンクから探す前提。 */}
        {!isMaster && (
          <SidebarGroup title="担当地区">
            {visibleDistricts.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-400">担当地区が割り当てられていません。</p>
            )}
            {visibleDistricts.map((d) => {
              const base = `/districts/${d.id}`;
              const expanded = currentDistrictId === d.id;
              return (
                <div key={d.id} className="mb-1">
                  <SidebarLink
                    to={base}
                    label={d.name}
                    active={expanded && isExactActive(pathname, base)}
                    onNavigate={onNavigate}
                    indent={0}
                  />
                  {expanded && (
                    <DistrictSubNav
                      districtId={d.id}
                      pathname={pathname}
                      onNavigate={onNavigate}
                      streetViewAnalysisEnabled={d.streetView?.analysisEnabled === true}
                      participantAdminVisible={
                        isMaster || d.participantAdmin?.districtAdminCanManage === true
                      }
                    />
                  )}
                </div>
              );
            })}
          </SidebarGroup>
        )}

        {/* 全地区 (master のみ) */}
        {isMaster && (
          <SidebarGroup title="全地区">
            <SidebarLink
              to="/districts"
              label="地区一覧"
              active={isExactActive(pathname, '/districts')}
              onNavigate={onNavigate}
            />
            {currentDistrictId && (
              <div className="mt-1">
                <p className="px-3 py-1 text-[10px] text-slate-400 uppercase tracking-wider">
                  選択中の地区
                </p>
                <SidebarLink
                  to={`/districts/${currentDistrictId}`}
                  label={
                    visibleDistricts.find((d) => d.id === currentDistrictId)?.name ??
                    currentDistrictId
                  }
                  active={isExactActive(pathname, `/districts/${currentDistrictId}`)}
                  onNavigate={onNavigate}
                />
                <DistrictSubNav
                  districtId={currentDistrictId}
                  pathname={pathname}
                  onNavigate={onNavigate}
                  streetViewAnalysisEnabled={
                    visibleDistricts.find((d) => d.id === currentDistrictId)?.streetView
                      ?.analysisEnabled === true
                  }
                  participantAdminVisible
                />
              </div>
            )}
          </SidebarGroup>
        )}

        {/* テンプレ・マスタ (master のみ) */}
        {isMaster && (
          <SidebarGroup title={TEMPLATE_GROUP.title}>
            {TEMPLATE_GROUP.subgroups?.map((sg) => (
              <div key={sg.title ?? 'default'} className="mb-2 last:mb-0">
                {sg.title && (
                  <p className="px-3 pt-1 pb-0.5 text-[10px] text-slate-400 uppercase tracking-wider">
                    {sg.title}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {sg.items.map((item) => (
                    <li key={item.to}>
                      <SidebarLink
                        to={item.to}
                        label={item.label}
                        active={isActive(pathname, item.to)}
                        onNavigate={onNavigate}
                        description={item.description}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </SidebarGroup>
        )}

        {/* 権限・監査 (master のみ) */}
        {isMaster && (
          <SidebarGroup title={AUDIT_GROUP.title}>
            {AUDIT_GROUP.items?.map((item) => (
              <SidebarLink
                key={item.to}
                to={item.to}
                label={item.label}
                active={isActive(pathname, item.to)}
                onNavigate={onNavigate}
                description={item.description}
              />
            ))}
          </SidebarGroup>
        )}

        {/* 使い方ガイド (全 role) */}
        <SidebarGroup title="サポート">
          <SidebarLink
            to={HELP_ITEM.to}
            label={HELP_ITEM.label}
            active={isActive(pathname, HELP_ITEM.to)}
            onNavigate={onNavigate}
            description={HELP_ITEM.description}
          />
        </SidebarGroup>
      </nav>

      <div className="px-4 py-3 border-t border-slate-200">
        <p className="text-xs text-slate-600 truncate" title={ctx.user?.email ?? ''}>
          <span className="font-semibold">{ctx.user?.email}</span>
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
            {ctx.role}
          </span>
          <Button variant="secondary" size="sm" onClick={() => void ctx.signOut()}>
            サインアウト
          </Button>
        </div>
      </div>
    </aside>
  );
}

/**
 * 地区サブメニュー (担当地区 / 選択中の地区 配下) を業務フロー順のグループ
 * (この地区について / 設定 / 運用 / データ) で描画する。
 * 単一の真実の源 `districtNavGroups` を参照し、概要は親リンク側で描画するため除外する。
 */
function DistrictSubNav({
  districtId,
  pathname,
  onNavigate,
  streetViewAnalysisEnabled,
  participantAdminVisible,
}: {
  districtId: string;
  pathname: string;
  onNavigate: () => void;
  /** docs/25 §10-1: この地区でSV机上調査が有効化されているか。false のとき机上調査項目を隠す。 */
  streetViewAnalysisEnabled?: boolean;
  /** ユーザー管理を出すか。master は常に true、地区管理者は地区設定 (master が許可) 次第。 */
  participantAdminVisible?: boolean;
}) {
  const groups = districtNavGroups(districtId, {
    streetViewAnalysisEnabled,
    participantAdminVisible,
  });
  return (
    <ul className="mt-0.5 ml-3 border-l border-slate-200 pl-2 space-y-1">
      {groups.map((g) => (
        <li key={g.group}>
          <p className="px-2 pt-1 pb-0.5 text-[10px] text-slate-400 uppercase tracking-wider">
            {g.group}
          </p>
          <ul className="space-y-0.5">
            {g.items.map((item) => (
              <li key={item.key}>
                <SidebarLink
                  to={item.to}
                  label={item.label}
                  active={isActive(pathname, item.to)}
                  onNavigate={onNavigate}
                  compact
                  description={item.description}
                />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function SidebarGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-5 last:mb-0">
      <h3 className="px-2 mb-1 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}

function SidebarLink({
  to,
  label,
  active,
  onNavigate,
  description,
  compact = false,
  indent: _indent = 0,
}: {
  to: string;
  label: string;
  active: boolean;
  onNavigate: () => void;
  description?: string;
  compact?: boolean;
  indent?: number;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      title={description}
      className={`
        block rounded-md transition-colors
        ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}
        ${active ? 'bg-primary-600 text-white font-medium' : 'text-slate-700 hover:bg-slate-100'}
      `}
    >
      {label}
    </Link>
  );
}

/**
 * 地区のサブページ (`/districts/:id/...`) の最下部に出す「← 地区詳細」リンク (2026-10-19)。
 * 表示設定など内容の長いページで、上部まで戻らずに地区詳細へ戻れるようにする。
 * 地区詳細そのもの (`/districts/:id`) には出さない。印刷時は出さない。
 */
export function districtBackFooterTarget(pathname: string): string | null {
  const districtId = extractDistrictIdFromPath(pathname);
  if (!districtId) return null;
  const base = `/districts/${districtId}`;
  return pathname === base || pathname === `${base}/` ? null : base;
}

function DistrictBackFooter({ pathname }: { pathname: string }) {
  const to = districtBackFooterTarget(pathname);
  if (!to) return null;
  return (
    <nav
      aria-label="地区詳細へ戻る"
      className="mt-8 pt-4 border-t border-slate-200 print:hidden"
      data-testid="district-back-footer"
    >
      <Link to={to} className="text-sm text-primary-600 hover:text-primary-700 hover:underline">
        ← 地区詳細
      </Link>
    </nav>
  );
}

export function AdminLayout({ ctx, children }: { ctx: AdminAuthContext; children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  // route 遷移時に mobile sidebar を閉じる
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar ctx={ctx} open={sidebarOpen} onNavigate={() => setSidebarOpen(false)} />

      {sidebarOpen && (
        <button
          type="button"
          aria-label="閉じる"
          className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
          <button
            type="button"
            aria-label="メニューを開く"
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <Link to="/" className="text-sm font-semibold text-slate-900">
            CitizenWalk Admin
          </Link>
          <span className="w-8" />
        </header>

        {/* card-print ページは A4 プレビュー + 左右ツールパレットで横幅を最大限使うため
            max-w-6xl 制約を外して全幅利用。それ以外は従来通り中央寄せ 1152px 上限。 */}
        <main
          className={
            pathname.includes('/card-print')
              ? 'flex-1 px-4 md:px-6 py-6 md:py-8 w-full'
              : 'flex-1 px-4 md:px-8 py-6 md:py-8 mx-auto w-full max-w-6xl'
          }
        >
          {/* Sprint BG-Menu (第 3 段): パンくず + (district admin で担当 2 件以上なら) 地区切替 */}
          <Breadcrumbs />
          <DistrictSwitcher ctx={ctx} />
          {children}
          <DistrictBackFooter pathname={pathname} />
        </main>

        <footer className="px-4 md:px-8 py-4 text-xs text-slate-400 border-t border-slate-200 bg-white">
          © Y.P.C. — Yamate Planning Cabin
        </footer>
      </div>

      {/* 新バージョン通知バナー (任意更新、2026-06-12)。画面下部に fixed で全ページ共通表示。 */}
      <UpdateBanner />
    </div>
  );
}
